<template>
	<Teleport to="body">
		<button
			v-if="!open"
			type="button"
			class="layers-fab"
			data-tour="layers"
			:title="t('title-layers')"
			:aria-label="t('title-layers')"
			@click="emit('update:open', true)"
		>
			<AppIcon :name="IconName.Hexagons" :size="20" />
		</button>

		<MobileDrawer
			v-model="collapsed"
			:active="open && !!peekSnapPoint"
			:peek-height="peekSnapPoint ?? '0px'"
			:reopen-title="t('title-layers')"
			:expand="isFilterViewOpen"
			@dismiss="emit('update:open', false)"
			v-slot="{ expanded }"
		>
			<Layers
				compact
				:expanded="expanded"
				:open="open && !collapsed"
				:data-tour-related="open ? 'layers' : undefined"
				:current-map-style="currentMapStyle"
				@current-map-style="emit('current-map-style', $event)"
				@header-resize="onHeaderResize"
				@filter-view="isFilterViewOpen = $event"
				@close="emit('update:open', false)"
			/>
		</MobileDrawer>
	</Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import type { MapStyleValue } from "@/components/constants"
import { IconName } from "@/components/icons/registry"
import Layers from "@/components/layers/Layers.vue"
import MobileDrawer from "@/components/layout/MobileDrawer.vue"

const props = defineProps<{
	currentMapStyle: MapStyleValue
	open: boolean
}>()

const emit = defineEmits<{
	"current-map-style": [style: MapStyleValue]
	"update:open": [open: boolean]
}>()

const { t } = useI18n()

const collapsed = ref(false)
const isFilterViewOpen = ref(false)
const peekSnapPoint = ref<string | null>(null)

function onHeaderResize(height: number): void {
	if (height > 0) peekSnapPoint.value = `${height}px`
}

watch(
	() => props.open,
	isOpen => {
		if (isOpen) collapsed.value = false
		else isFilterViewOpen.value = false
	}
)

// collapsing the sheet closes it (and brings the "Couches" button back)
watch(collapsed, value => {
	if (value) emit("update:open", false)
})
</script>

<style scoped>
.layers-fab {
	position: fixed;
	left: var(--space-12);
	bottom: calc(var(--space-56) + env(safe-area-inset-bottom) + var(--space-4));
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--space-40);
	height: var(--space-40);
	padding: 0;
	border: none;
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	color: var(--neutral-600);
	box-shadow: var(--shadow-lg);
	cursor: pointer;
}
</style>
